import React from 'react';
import styled from 'styled-components';
import { Box, Heading, Stack } from '@99designs/emu';
import { borders, colors, spaces, typography } from '@99designs/emu/settings';
import { CurrencyCode } from '@99designs/i18n';
import { Price } from '../../../components/Price';
import { snakeToKebabCase } from '../../../utils';
import { ChoiceCard } from '../../ChoiceCard';

type AccessibilityHeadingLevel = Exclude<typography.HeadingLevel, 0>;

export interface PackageCardProps {
    id: string;
    title: string;
    children: React.ReactChild;
    price?: Price;
    featureColor?: string;
    isSelected?: boolean;
    isSummaryCard?: boolean;
    onSelection?: (value: string) => void;
    tier?: 'bronze' | 'silver' | 'gold' | 'platinum';
    headingAccessibilityLevel: AccessibilityHeadingLevel;
}

interface Price {
    currency: CurrencyCode;
    amountInCents: number;
}

const StyledPill = styled.span<Pick<PackageCardProps, 'featureColor' | 'tier'>>`
    background-color: ${({ featureColor }) => featureColor};
    border-radius: ${borders.borderRadiuses.large}px;
    color: ${({ tier }) => (tier === 'platinum' ? colors.white : colors.grey800)};
    font-size: ${typography.typeScale.size1}px;
    font-weight: ${typography.fontWeights.bold};
    padding: ${spaces['0.5bu']} ${spaces['1bu']};
`;

export function PackageCard({
    id,
    title,
    children,
    price,
    featureColor = 'grey800',
    isSelected = false,
    isSummaryCard = false,
    onSelection,
    tier,
    headingAccessibilityLevel,
}: PackageCardProps) {
    return (
        <ChoiceCard
            isSelected={isSelected}
            color={featureColor as colors.Color}
            id={id}
            onSelection={onSelection}
            data-testid={`package-card-${snakeToKebabCase(id)}`}
        >
            <Stack space="1bu">
                <StyledPill tier={tier} featureColor={featureColor} title={title}>
                    {title}
                </StyledPill>
                {!isSummaryCard && price?.amountInCents && (
                    <Heading appearanceLevel={3} accessibilityLevel={headingAccessibilityLevel}>
                        <Price
                            amount={price.amountInCents}
                            currency={price.currency}
                            minimumFractionDigits={0}
                        />
                    </Heading>
                )}
                <Box>{children}</Box>
            </Stack>
        </ChoiceCard>
    );
}
